import { Dispatch, Middleware } from 'redux';
import { call, put, spawn, takeEvery } from 'redux-saga/effects';

import { languages$get$base } from '@/api/platform-data';
import { LanguageCode } from '@/i18n/languages';
import { Language } from '@/types/common';
import { pServiceFilterKeys } from '@/types/filters';
import { storageKeys } from '@/utils/constants';
import { Cookies } from '@/utils/cookies';
import { _window } from '@/utils/ssr-window';

import {
    filtersLoad,
    filtersLoadSuccess,
    pServiceFiltersActionTypes,
    pServiceFiltersKey,
    PServiceFiltersState,
} from './polako-service-filters.actions';
import { Filters, FiltersStored } from './polako-service-filters.types';

function* filtersLoad$(action: ReturnType<typeof filtersLoad>) {
    try {
        const language = Cookies.get(storageKeys.prefLanguage) as LanguageCode;
        const languages: Language[] = yield call(languages$get$base);
        const languagesTranslated = languages
            .filter((lang) => lang.code !== 'xx')
            .map((lang) => ({
                ...lang,
                title: lang.translations?.find((trans) => trans.language === language)?.nom ?? lang.title,
            }));

        const filters: Filters = {
            city: action.payload.cities.map((item) => ({ key: 'city', value: item.id, display: item.name })),
            language: languagesTranslated.map((item) => ({ key: 'language', value: item.code, display: item.title })),
            countryCode: action.payload.countryCode,
        };

        yield put(filtersLoadSuccess({ filters: filters }));
    } catch {}
}

function* pServiceFiltersLoadSaga() {
    yield takeEvery(pServiceFiltersActionTypes.filtersLoad, filtersLoad$);
}

export function* pServiceFiltersEffects() {
    yield spawn(pServiceFiltersLoadSaga);
}

export const pServiceFiltersStorageMw: Middleware<Dispatch, { [pServiceFiltersKey]: PServiceFiltersState }> =
    (api) => (next) => (action) => {
        const result = next(action);
        if (
            [
                pServiceFiltersActionTypes.filterSelect,
                pServiceFiltersActionTypes.filterDeselect,
                pServiceFiltersActionTypes.filtersReset,
            ].includes(action.type)
        ) {
            const newValue = api.getState()[pServiceFiltersKey].filtersSelected;
            const stored = {} as FiltersStored;
            for (const fKey of pServiceFilterKeys) {
                stored[fKey] = newValue[fKey].map((item) => item.value);
            }
            _window?.localStorage?.setItem(`${pServiceFiltersKey}_${newValue.countryCode}`, JSON.stringify(stored));
        }
        return result;
    };
